import React, {
    FunctionComponent,
    useState,
    useEffect,
    useCallback,
    useRef,
} from 'react';
import {
    Formik,
    FormikHelpers,
    FormikProps,
    Form as FormikForm
} from 'formik';

// MUI
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Snackbar from '@mui/material/Snackbar';

// Common
import api from '../../api';
import { GENERAL } from '../../constants';
import { models, types } from '../../types';

// Components
import { FormPersonal } from './components/FormPersonal';
import { FormSchool } from './components/FormSchool';
import { FormPrograms } from './components/FormPrograms';
import { FormSubjects } from './components/FormSubjects';
import { FormTerms } from './components/FormTerms';
import { Alert } from './components/Alert';

// Properties
import { validationSchema, defaultValues } from './Form.validation';
import * as styles from './Form.styles';

const Form: FunctionComponent = () => {
    const [ programs, setPrograms ] = useState<any[]>([]);
    const [ subjects, setSubjects ] = useState(null);
    const [ dictionaryError, setDictionaryError ] = useState('');
    const [ isSnackBarOpen, setIsSnackBarOpen, ] = useState(false);
    const [ isSaveError, setIsSaveError ] = useState<false | string>(false);
    const [ instructionLink, getInstruction ] = useState('');

    const formRef = useRef<FormikProps<types.TParticipant>>() as any;

    const fetchDictionary = () => {
        api.getDictionary()
            .then(({ data }) => {
                setPrograms(data.data.programs);
                setSubjects(data.data.subjects);
            })
            .catch(error => {
                setDictionaryError(error.message);
            });
    };

    const postParticipant = (model: models.TParticipant) => {
        api.postParticipant(model)
            .then(() => {
                setIsSnackBarOpen(true);
                setIsSaveError(false);
            })
            .catch((error) => {
                setIsSnackBarOpen(true);
                setIsSaveError(error.message);
            });
    };

    useEffect(() => {
        fetchDictionary();
    }, []);

    const onSubmit = ({ programId, schoolLevel, phoneNumber, ...values }: models.TParticipant) => {
        const valuesToCastNumber = ['schoolId', 'subjectId'];
        const castedValues = valuesToCastNumber.reduce((finalValues, prop) => ({
            ...finalValues,
            [prop]: Number(values[prop]),
        }), {});

        let phoneNumberValue = {};
        if (phoneNumber) {
            phoneNumberValue = {
                phoneNumber: phoneNumber,
            }
        }

        const preparePayload = {
            ...values,
            ...castedValues,
            ...phoneNumberValue,
            programName: programs?.find(program => program.id === Number(programId))?.name,
        };

        postParticipant(preparePayload as any);
        getInstruction(programs?.find(program => program.id === Number(programId))?.instructionLink);
    };

    const redirectOnSuccess = useCallback(() => {
        const REDIRECTION_URL_ON_SUCCESS = process.env['REACT_APP_REDIRECT_URL_ON_SUCCESS'];

        !isSaveError
        && formRef.current
        && formRef.current.isValid
        && REDIRECTION_URL_ON_SUCCESS
        && (window.location.href = REDIRECTION_URL_ON_SUCCESS);
    }, [formRef, isSaveError]);

    const handleClose = (event?: React.SyntheticEvent, reason?: string) => {
        if (reason === 'clickaway') return;
        redirectOnSuccess();
        setIsSnackBarOpen(false);
    };

    const handleClickAway = () => {
        redirectOnSuccess();
        setIsSnackBarOpen(false);
    };

    if (dictionaryError) {
        return (
            <Typography
                variant="body2"
                color="error"
            >
                {dictionaryError}
            </Typography>
        );
    }

    return (
        <Formik
            innerRef={formRef}
            initialValues={defaultValues}
            validationSchema={validationSchema}
            validateOnChange={false}
            validateOnBlur={false}
            validate={() => setIsSnackBarOpen(true)}
            onSubmit={(
                values: types.TParticipant,
                formikHelpers: FormikHelpers<types.TParticipant>
            ) => {
                onSubmit(values as unknown as any);
                formikHelpers.setSubmitting(false);
            }}
        >
            {(formikProps: FormikProps<types.TParticipant>) => (
                <>
                    <FormikForm noValidate autoComplete="off">
                        <FormPersonal />
                        <FormSchool />
                        {programs && <FormPrograms programs={programs} />}
                        {subjects && <FormSubjects subjects={subjects} />}
                        <FormTerms />
                        <Box sx={styles.action}>
                            <Button
                                variant="contained"
                                type="submit"
                                disabled={formikProps.isSubmitting}
                            >
                                {GENERAL.FORM_SUBMIT}
                            </Button>
                        </Box>
                    </FormikForm>
                    <ClickAwayListener
                        mouseEvent="onMouseDown"
                        touchEvent="onTouchStart"
                        onClickAway={handleClickAway}
                    >
                        <Snackbar
                            open={isSnackBarOpen}
                            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                            autoHideDuration={6000}
                            sx={styles.snackbar}
                        >
                            <Alert
                                onClose={handleClose}
                                severity={isSaveError || !formikProps.isValid ? 'error' : 'success'}
                            >
                                {!formikProps.isValid && GENERAL.FORM_VALIDATION}
                                {formikProps.isValid && (isSaveError
                                    ? isSaveError
                                    : <span>{GENERAL.FORM_SUCCESS[1]}<br />{GENERAL.FORM_SUCCESS[2]} <a href={instructionLink}>tutaj</a>.</span>)}
                            </Alert>
                        </Snackbar>
                    </ClickAwayListener>
                </>
            )}
        </Formik>
    );
};

export default Form;
